import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { refreshAccessToken } from "./auth";

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
});

// Endpoints that legitimately return 401 as a normal "bad credentials"
// response, not an "expired session" response. These must never trigger
// the refresh/redirect flow below.
const AUTH_ENDPOINTS = ["/auth/login"];
const isAuthEndpoint = (url) => AUTH_ENDPOINTS.some((endpoint) => url?.includes(endpoint));

api.interceptors.request.use(
    async (config) => {
        let token = localStorage.getItem("accessToken");
        const locationUid = localStorage.getItem("locationUid");

        // Refresh proactively when the token is already expired, instead of
        // relying solely on the reactive 401 handling below. Without this, a
        // request fired after the token expired while the tab sat idle (e.g.
        // navigating to a new route without a full page reload) would be the
        // first to notice, and only the one-time expiry check in AuthContext's
        // mount effect (i.e. a hard refresh) guaranteed a fresh token before.
        if (token && !isAuthEndpoint(config.url)) {
            try {
                if (jwtDecode(token).exp * 1000 < Date.now()) {
                    token = await refreshAccessToken();
                }
            } catch {
                // Malformed token or refresh failure - let the request go
                // through as-is and fall back to the response interceptor.
            }
        }

        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }

        if (locationUid) {
            config.headers["X-Location-UID"] = locationUid;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint(originalRequest.url)) {
            originalRequest._retry = true;

            try {
                const newToken = await refreshAccessToken();
                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (err) {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("locationUid");
                localStorage.removeItem("locations");
                window.location.href = "/login";
                return Promise.reject(err);
            }
        }

        return Promise.reject(error);
    }
);

export default api;
